import { tabType, InterfaceTab } from '#/types/types';

export default class TabManager {
    private ircClient: any;
    private tabs: Map<string, InterfaceTab>;
    public channelList: any[] = [];
    public channelListUpdating = false;

    constructor(ircClient: any) {
        this.ircClient = ircClient;
        this.tabs = new Map<string, InterfaceTab>();
    }

    initialize(): void {
        this.addTab(tabType.SETTINGS);
    }

    addTab(type: tabType, name?: string): InterfaceTab {
        let tabName = name || 'Unknown';
        if (type === tabType.STATUS) { tabName = 'Status'; }
        else if (type === tabType.SETTINGS) { tabName = 'Settings'; }
        else if (type === tabType.CHANNEL_LIST) { tabName = 'Channels'; }
        const tab: InterfaceTab = {
            key: type + ':' + tabName.toLowerCase(),
            name: tabName,
            type: type,
            messages: [],
            users: type === tabType.CHANNEL ? [] : null
        };

        if (this.tabs.has(tab.key)) {
            this.setActiveTab(tab);
            return tab;
        }

        this.tabs.set(tab.key, tab);
        this.ircClient.uiRenderer.renderTab(tab);
        this.ircClient.uiRenderer.renderTabContent(tab);
        this.setActiveTab(tab);

        return tab;
    }

    closeTab(name: string, type?: tabType): void {
        if (type) {
            const tab = this.getTab(type, name);
            if (tab) {
                this.removeTab(tab);
            }
        } else {
            let tab = this.getTab(tabType.CHANNEL, name);
            if (!tab) {
                tab = this.getTab(tabType.QUERY, name);
            }
            if (tab) {
                this.removeTab(tab);
            }
        }
    }

    private removeTab(tab: InterfaceTab): void {
        if (!tab || tab.type === tabType.SETTINGS) { return; }

        if (tab.type === tabType.CHANNEL) {
            this.ircClient.connectionHandler.partChannel(tab.name, 'Leaving');
        }
        tab.messages = [];
        this.ircClient.uiRenderer.removeTabElement(tab);
        this.ircClient.uiRenderer.removeTabContent(tab);

        this.ircClient.messageHandler.messageContainers.delete(tab.name);

        const key = tab.type + ':' + tab.name.toLowerCase();
        this.tabs.delete(key);

        if (this.ircClient.activeTab === tab) {
            this.switchToDefaultTab();
        }
    }

    setActiveTab(tab: InterfaceTab): void {
        if (!tab) return;

        this.ircClient.activeTab = tab;
        this.ircClient.uiRenderer.updateActiveTab(tab);
    }

    getActiveTab(): InterfaceTab {
        return this.ircClient.activeTab;
    }

    getTab(type: tabType, name: string): InterfaceTab | null {
        const key = type + ':' + name.toLowerCase();
        return this.tabs.get(key) || null;
    }

    getStatusTab(): InterfaceTab {
        return this.tabs.get(tabType.STATUS + ':status')!;
    }

    getSettingsTab(): InterfaceTab {
        return this.tabs.get(tabType.SETTINGS + ':settings')!;
    }

    getChannelListTab(): InterfaceTab | undefined {
        return this.tabs.get(tabType.CHANNEL_LIST + ':channel');
    }

    updateTabName(oldName: string, newName: string): void {
        let tab: InterfaceTab | undefined;
        let oldKey: string | undefined;
        for (const [key, t] of this.tabs) {
            if (t.name === oldName) {
                tab = t;
                oldKey = key;
                break;
            }
        }
        if (!tab || !oldKey) return;

        tab.name = newName;

        this.tabs.delete(oldKey);
        const newKey = tab.type + ':' + newName.toLowerCase();
        this.tabs.set(newKey, tab);

        this.ircClient.uiRenderer.updateTabElementName(oldKey, newKey, newName);

        if (this.ircClient.activeTab?.name === oldName) {
            this.ircClient.activeTab.name = newName;
        }
    }

    closeAllTabs(): void {
        const tabsToClose: InterfaceTab[] = [];

        this.tabs.forEach((tab: InterfaceTab) => {
            if (tab && tab.type !== tabType.SETTINGS) {
                tabsToClose.push(tab);
            }
        });

        tabsToClose.reverse().forEach(tab => {
            this.removeTab(tab);
        });
    }

    getAllTabs(): Map<string, InterfaceTab> {
        return this.tabs;
    }

    getAllChannelTabs(): Map<string, InterfaceTab> {
        const channelTabs = new Map<string, InterfaceTab>();
        this.tabs.forEach((tab, key) => {
            if (tab.type === tabType.CHANNEL) {
                channelTabs.set(key, tab);
            }
        });
        return channelTabs;
    }

    switchToDefaultTab(): void {
        const statusTab = this.getStatusTab();
        if (statusTab) {
            this.setActiveTab(statusTab);
        } else {
            const settingsTab = this.getSettingsTab();
            if (settingsTab) {
                this.setActiveTab(settingsTab);
            }
        }
    }
    
    renderChannelList(): void {
        this.addTab(tabType.CHANNEL_LIST);
        const channelListContent = document.querySelector('.irc-channel-list-container') as HTMLElement;
        if (!channelListContent) return;

        channelListContent.innerHTML = '';

        if (!this.channelList || this.channelList.length === 0) {
            return;
        }

        this.channelList.sort((a, b) => b.num_users - a.num_users);

        this.channelList.forEach(c => {
            const channelElement = document.createElement('div');
            channelElement.className = 'irc-channel-list-item';

            const nameElement = document.createElement('span');
            nameElement.className = 'channel-name';
            nameElement.textContent = c.channel;

            const usersElement = document.createElement('span');
            usersElement.className = 'channel-users';

            usersElement.textContent = `${c.num_users} users`;

            const topicElement = document.createElement('span');
            topicElement.className = 'channel-topic';
            topicElement.textContent = c.topic || 'No topic';

            channelElement.appendChild(nameElement);
            channelElement.appendChild(usersElement);
            channelElement.appendChild(topicElement);

            channelElement.addEventListener('click', () => {
                this.ircClient.connectionHandler.joinChannel(c.channel);
            });

            channelListContent.appendChild(channelElement);
        });
    }
}